import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography,
  CircularProgress, Alert
} from '@mui/material';
import {
  Pets, DeviceThermostat, Warning
} from '@mui/icons-material';
import { getDogs, getInventoryAlerts, getColdChainAlerts } from '../api/client';
import { useAuth } from '../context/AuthContext';

function StatCard({ icon, label, value, color }) {
  return (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2,
          background: `${color}20`,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {React.cloneElement(icon, { sx: { color } })}
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    dogs: 0, inventoryAlerts: 0, coldChainBreaches: 0
  });
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getDogs(), getInventoryAlerts(), getColdChainAlerts()])
      .then(([dogsRes, invRes, ccRes]) => {
        setStats({
          dogs: dogsRes.data.length,
          inventoryAlerts: invRes.data.total_alerts,
          coldChainBreaches: ccRes.data.total_breaches_24h
        });
        const allAlerts = [
          ...invRes.data.alerts.map(a => ({ ...a, source: 'inventory' })),
          ...ccRes.data.breaches.slice(0, 3).map(b => ({
            type: 'breach',
            severity: 'critical',
            message: `Device ${b.device_id}: ${b.temperature_c}°C`,
            source: 'coldchain'
          }))
        ];
        setAlerts(allAlerts);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Welcome, {user?.full_name?.split(' ')[0]}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {new Date().toLocaleDateString('en-KE', {
          weekday: 'long', year: 'numeric',
          month: 'long', day: 'numeric'
        })}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <StatCard
            icon={<Pets />} label="Registered Dogs"
            value={stats.dogs} color="#5BA4D4"
          />
        </Grid>
        <Grid item xs={6}>
          <StatCard
            icon={<Warning />} label="Stock Alerts"
            value={stats.inventoryAlerts} color="#FF9800"
          />
        </Grid>
        <Grid item xs={12}>
          <StatCard
            icon={<DeviceThermostat />} label="Cold Chain Breaches (24h)"
            value={stats.coldChainBreaches} color="#F44336"
          />
        </Grid>
      </Grid>

      {alerts.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Active Alerts
          </Typography>
          {alerts.map((alert, i) => (
            <Alert
              key={i}
              severity={alert.severity === 'critical' ? 'error' : 'warning'}
              sx={{ mb: 1, borderRadius: 2 }}
            >
              {alert.message}
            </Alert>
          ))}
        </>
      )}
    </Box>
  );
}
